import React, { useEffect, useRef, useState } from 'react';
import './LandingPage.css';
import useReveal from './useReveal';

/* ---------------- inline icons (no external requests) ---------------- */

const IconStethoscope = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}>
    <path d="M4.5 3v5a4 4 0 0 0 8 0V3" strokeLinecap="round" />
    <path d="M8.5 12v2.5a5.5 5.5 0 0 0 11 0V13" strokeLinecap="round" />
    <circle cx="19.5" cy="11" r="2.2" />
    <path d="M3 3h3M11 3h3" strokeLinecap="round" />
  </svg>
);

const IconScan = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}>
    <path d="M3 8V5a2 2 0 0 1 2-2h3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M21 16v3a2 2 0 0 1-2 2h-3" strokeLinecap="round" />
    <path d="M3 12h18" strokeLinecap="round" />
  </svg>
);

const IconPlan = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}>
    <path d="M9 3h6l1 3H8l1-3Z" strokeLinejoin="round" />
    <rect x="4" y="6" width="16" height="15" rx="2.5" />
    <path d="M9 12h6M9 16h4" strokeLinecap="round" />
  </svg>
);

const IconCalendar = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2.5" />
    <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
    <circle cx="8.5" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

const IconPin = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}>
    <path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z" strokeLinejoin="round" />
    <path d="M12 7.5v5M9.5 10h5" strokeLinecap="round" />
  </svg>
);

const IconArrow = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ---------------- count-up for the stat strip ---------------- */

function useCountUp(target, duration = 1600) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      setValue(target);
      return undefined;
    }

    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          setValue(target * eased);
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );

    io.observe(node);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [target, duration]);

  return [ref, value];
}

function Stat({ value, decimals = 0, suffix = '', label }) {
  const [ref, current] = useCountUp(value);
  return (
    <div className="stat" ref={ref}>
      <div className="stat-value gradient-text">
        {current.toFixed(decimals)}
        <span className="stat-suffix">{suffix}</span>
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/* ---------------- rotating headline word ---------------- */

const ROTATING = ['health assistant', 'symptom checker', 'care planner', 'recovery coach'];

function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % ROTATING.length), 2800);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="rotator">
      <span className="rotator-word gradient-text" key={i}>
        {ROTATING[i]}
      </span>
    </span>
  );
}

/* ---------------- character-stagger heading ---------------- */

function SplitText({ text, className = '', start = 0 }) {
  return (
    <span className={className}>
      {text.split('').map((ch, i) => (
        <span className="char" key={i} style={{ animationDelay: `${start + i * 22}ms` }}>
          {ch}
        </span>
      ))}
    </span>
  );
}

/* ---------------- magnetic button wrapper ---------------- */

function Magnetic({ children, className = '', ...rest }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - (r.left + r.width / 2);
    const y = e.clientY - (r.top + r.height / 2);
    el.style.transform = `translate(${x * 0.18}px, ${y * 0.28}px)`;
  };

  const onLeave = () => {
    if (ref.current) ref.current.style.transform = '';
  };

  return (
    <button
      ref={ref}
      className={`magnetic ${className}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ---------------- tilt-on-hover card ---------------- */

function TiltCard({ children, className = '', style }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.setProperty('--ry', `${(px - 0.5) * 14}deg`);
    el.style.setProperty('--rx', `${(0.5 - py) * 14}deg`);
    el.style.setProperty('--gx', `${px * 100}%`);
    el.style.setProperty('--gy', `${py * 100}%`);
  };

  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--rx', '0deg');
  };

  return (
    <div
      ref={ref}
      className={`tilt ${className}`}
      style={style}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </div>
  );
}

/* ---------------- content ---------------- */

const FEATURES = [
  {
    icon: IconScan,
    title: 'Plain-English Input',
    body: 'Describe symptoms in your own words. A fine-tuned sentence encoder maps free text onto 132 clinical markers — no dropdowns, no exact wording required.',
    tone: 'c1',
  },
  {
    icon: IconPlan,
    title: 'Past the Training Set',
    body: 'A classifier only knows its 105 training labels. Semantic retrieval over a curated knowledge base surfaces what it cannot — including appendicitis and meningitis.',
    tone: 'c2',
  },
  {
    icon: IconCalendar,
    title: 'Triage That Leads',
    body: 'Time-critical patterns are ranked by consequence, not likelihood, so an emergency is never buried under a confident-looking guess.',
    tone: 'c3',
  },
  {
    icon: IconPin,
    title: 'Pharmacies Near You',
    body: 'Enter a pincode and get real medical stores in your area, matched from a nationwide dataset.',
    tone: 'c4',
  },
];

const STEPS = [
  { n: '01', title: 'Write it in plain English', body: 'No checkboxes. A fine-tuned MiniLM encoder plus a 737-term lexicon maps your wording onto clinical markers, and shows you exactly what it understood.' },
  { n: '02', title: 'Two models, then retrieval', body: 'A symptom-profile scorer and a Random Forest are ensembled, because a forest reads unmentioned symptoms as absent. Retrieval then looks beyond its 105 labels.' },
  { n: '03', title: 'Triage, then a plan', body: 'Urgency first, then medications, precautions, a 7-day diet and exercise plan, calendar reminders and pharmacies near you.' },
];

const FLOATING = ['high fever', 'headache', 'fatigue', 'skin rash', 'cough', 'nausea'];

const CONDITIONS = [
  'Common Cold', 'Migraine', 'Allergy', 'Diabetes', 'Bronchial Asthma', 'Malaria',
  'Dengue', 'Typhoid', 'Jaundice', 'Hypertension', 'Gastroenteritis', 'Pneumonia',
  'Arthritis', 'Chicken pox', 'Psoriasis', 'Hepatitis', 'Tuberculosis', 'Vertigo',
  'Acne', 'GERD', 'Urinary tract infection', 'Impetigo',
];

function LandingPage({ onStart }) {
  const [scrolled, setScrolled] = useState(false);
  const visualRef = useRef(null);
  useReveal([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Parallax: the hero visual leans toward the cursor.
  useEffect(() => {
    const el = visualRef.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const onMove = (e) => {
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;
      el.style.setProperty('--px', `${x * 26}px`);
      el.style.setProperty('--py', `${y * 20}px`);
      el.style.setProperty('--tilt', `${-x * 8}deg`);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  return (
    <div className="landing">
      {/* ---------------- nav ---------------- */}
      <nav className={`nav ${scrolled ? 'nav-solid' : ''}`}>
        <div className="nav-inner">
          <div className="brand">
            <span className="brand-mark">
              <IconStethoscope width="20" height="20" />
              <i className="brand-pulse" />
            </span>
            <span className="brand-name">
              Health<span className="gradient-text">AI</span>
            </span>
          </div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
          </div>
          <button className="btn btn-ghost nav-cta" onClick={onStart}>
            Launch app
            <IconArrow width="16" height="16" />
          </button>
        </div>
      </nav>

      {/* ---------------- hero ---------------- */}
      <header className="hero">
        <div className="hero-copy">
          <div className="badge reveal">
            <span className="badge-dot" />
            Fine-tuned encoder · ensemble · 135 conditions
          </div>

          <h1 className="hero-title">
            {['Your', 'personalized'].map((w, i) => (
              <span className="word" style={{ '--d': `${i * 90}ms` }} key={w}>
                {w}
              </span>
            ))}
            <RotatingWord />
          </h1>

          <p className="hero-sub reveal" style={{ '--delay': '120ms' }}>
            Describe your symptoms in plain language — however you'd actually say it. Get a triaged
            assessment, medications and precautions, a 7-day diet and exercise plan, calendar
            reminders, and pharmacies near you.
          </p>

          <div className="hero-actions reveal" style={{ '--delay': '220ms' }}>
            <Magnetic className="btn btn-primary btn-lg shine" onClick={onStart}>
              <span>Start health check</span>
              <IconArrow width="18" height="18" />
            </Magnetic>
            <a className="btn btn-ghost btn-lg" href="#how">
              See how it works
            </a>
          </div>

          <div className="hero-trust reveal" style={{ '--delay': '300ms' }}>
            <span className="tick">✓</span> Free · No sign-up · Results in seconds
          </div>
        </div>

        {/* ---------------- hero visual ---------------- */}
        <div className="hero-visual" aria-hidden="true" ref={visualRef}>
          <div className="orb-stage">
            <div className="orb-ring r1" />
            <div className="orb-ring r2" />
            <div className="orb-ring r3" />
            <div className="orb-glow" />

            {/* dots orbiting the core */}
            <span className="orbit-dot o1" />
            <span className="orbit-dot o2" />
            <span className="orbit-dot o3" />

            <div className="orb-core">
              <IconStethoscope width="52" height="52" />
              <span className="ping" />
              <span className="ping ping2" />
            </div>

            {FLOATING.map((label, i) => (
              <span
                className={`chip-float chip-${i}`}
                key={label}
                style={{ '--d': `${i * 0.7}s` }}
              >
                {label}
              </span>
            ))}
          </div>

          <div className="ecg-card glass">
            <div className="ecg-head">
              <span className="ecg-dot" />
              Live signal
            </div>
            <svg className="ecg" viewBox="0 0 300 70" preserveAspectRatio="none">
              <path
                d="M0,35 L40,35 L52,35 L60,12 L70,58 L80,35 L110,35 L120,26 L130,44 L140,35 L180,35 L192,35 L200,10 L210,60 L220,35 L260,35 L300,35"
                fill="none"
                stroke="url(#ecgGrad)"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <defs>
                <linearGradient id="ecgGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#22d3ee" />
                  <stop offset="60%" stopColor="#818cf8" />
                  <stop offset="100%" stopColor="#f472b6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      </header>

      {/* ---------------- conditions marquee ---------------- */}
      <section className="marquee-wrap reveal" aria-hidden="true">
        <span className="marquee-label">Trained to recognise</span>
        <div className="marquee">
          <div className="marquee-track">
            {[...CONDITIONS, ...CONDITIONS].map((c, i) => (
              <span className="marquee-item" key={i}>
                <span className="marquee-dot" />
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- stats ---------------- */}
      <section className="stats reveal">
        <Stat value={135} label="Conditions covered" />
        <Stat value={132} label="Clinical markers" />
        <Stat value={20} suffix=" pts" label="Encoder accuracy gain" />
        <Stat value={23} suffix=" MB" label="On-device model" />
      </section>

      {/* ---------------- features ---------------- */}
      <section className="section" id="features">
        <div className="section-head reveal">
          <span className="eyebrow">What you get</span>
          <h2>
            <SplitText text="Everything after the " />
            <SplitText text="diagnosis" className="gradient-text" start={420} />
          </h2>
          <p>
            Most symptom checkers stop at a guess. This one hands you the whole plan — and the
            pharmacy that stocks it.
          </p>
        </div>

        <div className="feature-grid">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <TiltCard
                key={f.title}
                className={`feature-card reveal tone-${f.tone}`}
                style={{ '--delay': `${i * 90}ms` }}
              >
                <div className="feature-glow" />
                <span className="feature-icon">
                  <Icon width="24" height="24" />
                </span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </TiltCard>
            );
          })}
        </div>
      </section>

      {/* ---------------- how it works ---------------- */}
      <section className="section" id="how">
        <div className="section-head reveal">
          <span className="eyebrow">How it works</span>
          <h2>
            <SplitText text="Three steps, " />
            <SplitText text="about ten seconds" className="gradient-text" start={280} />
          </h2>
        </div>

        <div className="steps">
          <div className="steps-line" />
          {STEPS.map((s, i) => (
            <div className="step reveal" key={s.n} style={{ '--delay': `${i * 130}ms` }}>
              <div className="step-num">{s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- cta ---------------- */}
      <section className="cta-band reveal">
        <div className="cta-inner glass">
          <h2>
            Ready to check <span className="gradient-text">how you feel?</span>
          </h2>
          <p>No account, no forms to fill out. Just your symptoms and a pincode.</p>
          <Magnetic className="btn btn-primary btn-lg shine" onClick={onStart}>
            <span>Start health check</span>
            <IconArrow width="18" height="18" />
          </Magnetic>
        </div>
      </section>

      {/* ---------------- footer ---------------- */}
      <footer className="footer">
        <div className="disclaimer">
          <strong>Medical disclaimer.</strong> Health AI is an educational machine-learning project.
          It is not a medical device and does not provide medical advice, diagnosis or treatment.
          Always consult a qualified healthcare professional before acting on anything you see here.
          In an emergency, contact your local emergency services.
        </div>
        <div className="footer-bottom">
          <div className="brand small">
            <span className="brand-mark">
              <IconStethoscope width="16" height="16" />
            </span>
            <span className="brand-name">
              Health<span className="gradient-text">AI</span>
            </span>
          </div>
          <span>© {new Date().getFullYear()} Health AI · Built with React &amp; scikit-learn</span>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
